import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import {
  Group,
  Text,
  Button,
  TextInput,
  Container,
  Flex,
  useMantineTheme,
  Anchor,
  Box,
  Stepper,
  RingProgress,
  Checkbox,
} from "@mantine/core";
import {
  IconXboxX,
  IconFolderOpen,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import LogoAwsSvg from "../svg/LogoAwsSvg";
import { Dropzone } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  foundCollectionFetch,
  handleDropProcess,
} from "../utils/collectionFound/general";
import { useMediaQuery } from "@mantine/hooks";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";

import CustomButton from "./mantineCustomComponents/CustomButton";
import {
  URLs,
  validateS3Credentials,
  checkS3ValidationStatus,
  checkAuthentication,
} from "../utils/apis";

import { useStylesGeneral } from "../utils/mantine/stampTabContent/useStylesGeneral";
import { useStylesCollectionSelect } from "../utils/mantine/stampTabContent/useStylesCollectionSelect";

import Cookies from "js-cookie";
import {
  FILE_SIZE_WARNING_BYTES,
  FILE_SIZE_HARD_LIMIT_BYTES,
  FILE_SIZE_HARD_LIMIT_TITLE,
  FILE_SIZE_VALIDATION_HARD_LIMIT_MESSAGE,
  FILE_SIZE_VALIDATION_WARNING_TITLE,
  FILE_SIZE_VALIDATION_WARNING_MESSAGE,
} from "../utils/fileSizeConstants";

// Resource types constants
const RESOURCE_TYPES = ["archive", "s3-link", "s3-credentials"];

const hasAnyS3FieldValue = (credentials) =>
  [
    credentials.bucketName,
    credentials.folderPath,
    credentials.accessKeyId,
    credentials.secretAccessKey,
    credentials.region,
  ].some((value) => value && value.trim());

const parseS3UriToCredentials = (uri, currentCredentials) => {
  const trimmedUri = uri.trim();
  if (!trimmedUri.startsWith("s3://")) {
    return null;
  }

  try {
    const withoutScheme = trimmedUri.slice("s3://".length);
    const [pathWithHost, queryString = ""] = withoutScheme.split("?");
    const [hostPart = "", ...pathParts] = pathWithHost.split("/");

    let bucketName = "";
    let folderPath = "";

    // Case 1: Access Point ARN (host is arn, first path segment is access point name)
    if (hostPart.startsWith("arn:aws:s3:")) {
      if (pathParts.length === 0) {
        return null;
      }
      const accessPointName = pathParts[0];
      bucketName = `${hostPart}/${accessPointName}`;
      const remainder = pathParts.slice(1).join("/");
      if (remainder) {
        folderPath = remainder.endsWith("/") ? remainder : `${remainder}/`;
      }
    }
    // Case 2: Endpoint-style (legacy): s3.<region>.amazonaws.com:bucket/path
    else if (hostPart.startsWith("s3.") && hostPart.includes(":")) {
      const [, bucket] = hostPart.split(":", 2);
      bucketName = bucket || "";
      const path = pathParts.join("/");
      if (path) {
        folderPath = path.endsWith("/") ? path : `${path}/`;
      }
    }
    // Case 3: Normal bucket
    else {
      bucketName = hostPart;
      const path = pathParts.join("/");
      if (path) {
        folderPath = path.endsWith("/") ? path : `${path}/`;
      }
    }

    const searchParams = new URLSearchParams(queryString);
    const accessKeyId = (searchParams.get("access") || "").replace(/ /g, "+");
    const secretAccessKey = (searchParams.get("secret") || "").replace(
      / /g,
      "+",
    );
    const sessionToken = (searchParams.get("token") || "").replace(/ /g, "+");
    const region = (searchParams.get("region") || "").trim() || "us-east-1";

    return {
      ...currentCredentials,
      bucketName,
      folderPath,
      accessKeyId,
      secretAccessKey,
      ...(sessionToken && { sessionToken }),
      region,
    };
  } catch {
    return null;
  }
};

const buildS3UriFromCredentials = (credentials) => {
  if (!hasAnyS3FieldValue(credentials)) {
    return "";
  }

  const bucketName = credentials.bucketName?.trim() || "";
  const folderPath = credentials.folderPath?.trim() || "";
  const normalizedFolder = folderPath
    ? (() => {
        const withoutLeading = folderPath.replace(/^\/+/, "");
        return withoutLeading.endsWith("/")
          ? withoutLeading
          : `${withoutLeading}/`;
      })()
    : "";

  const region = credentials.region?.trim() || "us-east-1";
  const queryParams = new URLSearchParams();
  if (credentials.accessKeyId?.trim()) {
    queryParams.set("access", credentials.accessKeyId.trim());
  }
  if (credentials.secretAccessKey?.trim()) {
    queryParams.set("secret", credentials.secretAccessKey.trim());
  }
  if (credentials.sessionToken?.trim()) {
    queryParams.set("token", credentials.sessionToken.trim());
  }
  if (region) {
    queryParams.set("region", region);
  }

  let baseUri = "";

  // Access Point ARN: bucketName contains arn.../ap-name
  if (bucketName.startsWith("arn:aws:s3:") && bucketName.includes("/")) {
    const lastSlashIndex = bucketName.lastIndexOf("/");
    const arnHost = bucketName.slice(0, lastSlashIndex);
    const accessPointName = bucketName.slice(lastSlashIndex + 1);
    const pathSegment = normalizedFolder
      ? `${accessPointName}/${normalizedFolder}`
      : accessPointName;
    baseUri = `s3://${arnHost}/${pathSegment}`;
  } else {
    const pathSegment = normalizedFolder ? `/${normalizedFolder}` : "";
    baseUri = `s3://${bucketName}${pathSegment}`;
  }

  const queryString = queryParams.toString();
  return queryString ? `${baseUri}?${queryString}` : baseUri;
};

const VerifyCollection = ({
  props,
  onShowCollectionFound,
  onContentCollectionFound,
  onFileName,
  onErrorMessage,
  onFileType,
  isAuthenticated,
  setIsAuthenticated,
}) => {
  const { classes: stampWrapClasses } = useStylesGeneral();
  const { classes: collectionSelectClasses } = useStylesCollectionSelect();

  const [files, setFiles] = useState([]);
  const [newData, setNewData] = useState("");
  const [loading, setLoading] = useState(false);

  const [s3Credentials, setS3Credentials] = useState({
    region: "us-east-1",
    bucketName: "",
    folderPath: "",
    accessKeyId: "",
    secretAccessKey: "",
  });
  const [s3ValidationLoading, setS3ValidationLoading] = useState(false);
  const [s3ValidationProgress, setS3ValidationProgress] = useState(null);
  const [s3JobId, setS3JobId] = useState(null);
  const [s3ProcessingStatus, setS3ProcessingStatus] = useState(null);
  const [s3ProcessingActiveStep, setS3ProcessingActiveStep] = useState(0);
  const [s3ValidationResult, setS3ValidationResult] = useState(null); // Store validation result
  const notificationShownRef = useRef(false); // Track if success notification was shown
  const pollingActiveRef = useRef(false); // Track if polling is active
  const [s3CredentialsErrors, setS3CredentialsErrors] = useState({
    region: "",
    bucketName: "",
    folderPath: "",
    accessKeyId: "",
    secretAccessKey: "",
    general: "",
  });
  const [s3Uri, setS3Uri] = useState(""); // S3 URI for collection_uri field
  const [s3UriError, setS3UriError] = useState("");
  const [showManualInput, setShowManualInput] = useState(false); // Toggle between URI and manual form
  const [resourceType, setResourceType] = useState(() => {
    const cookieResourceType = Cookies.get("resourceType");

    let type;
    if (cookieResourceType !== undefined) {
      type = JSON.parse(cookieResourceType);
    }

    return RESOURCE_TYPES.includes(type) ? type : "archive";
  }); // archive, s3-link, s3-credentials

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  const tabletMediaQuery = useMediaQuery("(max-width: 64em)");

  // Handle URL parameters and hash changes
  useEffect(() => {
    const handleLocationChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const collectionMethodParam = urlParams.get("collectionMethod");

      // Check if the collectionMethod param contains a valid resource type
      if (
        collectionMethodParam &&
        RESOURCE_TYPES.includes(collectionMethodParam)
      ) {
        setResourceType(collectionMethodParam);
        Cookies.set("resourceType", JSON.stringify(collectionMethodParam));
      }
    };

    window.addEventListener("popstate", handleLocationChange);
    handleLocationChange();

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  const handleDrop = async (droppedFiles) => {
    if (droppedFiles.length > 0) {
      const [firstFile] = droppedFiles;

      if (firstFile.size > FILE_SIZE_HARD_LIMIT_BYTES) {
        setFiles([]);
        setNewData("");
        form.reset();
        notifications.show({
          title: FILE_SIZE_HARD_LIMIT_TITLE,
          message: FILE_SIZE_VALIDATION_HARD_LIMIT_MESSAGE,
          color: "red",
        });
        return;
      }

      if (firstFile.size >= FILE_SIZE_WARNING_BYTES) {
        notifications.show({
          title: FILE_SIZE_VALIDATION_WARNING_TITLE,
          message: FILE_SIZE_VALIDATION_WARNING_MESSAGE,
          color: "yellow",
        });
      }

      const fileType = firstFile.type;
      if (fileType) {
        onFileType(fileType); // Pass the file type to the callback
      }

      await handleDropProcess(
        droppedFiles,
        setFiles,
        form,
        setNewData,
        onErrorMessage,
      );
    }
  };

  // Previews (only one file)
  const preview = files.length > 0 ? files[0].name : null;

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      new_csv: "",
    },
  });

  //Submit form
  const handleFormSubmit = async (values) => {
    try {
      // Check authentication before making request
      const authStatus = await checkAuthentication(setIsAuthenticated);

      if (authStatus === false) {
        // User is not authenticated - don't proceed
        return;
      }

      setLoading(true);

      const csrfToken = document.querySelector(
        "[name=csrfmiddlewaretoken]",
      ).value;

      try {
        const result = await foundCollectionFetch(values.new_csv, csrfToken);

        if (result.success) {
          onFileName(preview);
          setFiles([]);
          setNewData("");
          form.reset();
          onShowCollectionFound(true);
          onContentCollectionFound(result.data);
        } else {
          console.error(result.error);
          notifications.show({
            title: "Error",
            message: result.error,
            color: "red",
          });
          onErrorMessage(result.error);
        }
      } catch (error) {
        console.error("Error:", error);
        notifications.show({
          title: "Error",
          message: "Error uploading CSV",
          color: "red",
        });
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  // Remove file
  const handleClickRemoveFile = () => {
    form.reset();
    setFiles([]);
    setNewData("");
  };

  // Handle resource type change
  const handleResourceTypeChange = (type) => {
    setResourceType(type);
    Cookies.set("resourceType", JSON.stringify(type));

    // Update URL with collectionMethod parameter
    const url = new URL(window.location);
    url.searchParams.set("collectionMethod", type);
    window.history.pushState({}, "", url);

    // Reset form when switching resource types
    if (type !== "archive") {
      form.reset();
      setFiles([]);
      setNewData("");
    }
  };

  // Map job status to stepper step
  const getStepFromStatus = (status) => {
    if (!status) return 0;

    switch (status) {
      case "pending":
        return 0; // Initial state
      case "loading":
        return 1; // Loading files from S3
      case "processing":
        return 2; // Processing files
      case "ready":
        return 3; // Ready for verification (or completed)
      case "failed":
        return 0; // Reset on error
      default:
        return 0;
    }
  };

  // Poll for job status - sequential polling (wait for response before next request)
  useEffect(() => {
    if (
      !s3JobId ||
      s3ProcessingStatus === "ready" ||
      s3ProcessingStatus === "failed"
    ) {
      pollingActiveRef.current = false;
      return;
    }

    pollingActiveRef.current = true;
    let pollTimeout = null;
    // We rely on sequential polling (next poll scheduled only after previous completes),
    let shouldContinuePolling = true; // Flag to control if we should continue

    const poll = async () => {
      // Check if we should stop polling before making request
      if (!pollingActiveRef.current || !shouldContinuePolling) {
        return;
      }

      try {
        await checkS3ValidationStatus(
          s3JobId,
          null, // setLoading - not needed for polling
          (data) => {
            // Check if there's an error in the response
            if (data.error) {
              // Error in response - treat as error, not success
              pollingActiveRef.current = false;
              shouldContinuePolling = false;
              if (pollTimeout) {
                clearTimeout(pollTimeout);
                pollTimeout = null;
              }

              setS3ProcessingStatus("failed");
              setS3ValidationLoading(false);
              setS3ProcessingActiveStep(0);
              setS3ValidationResult(null); // Clear any previous result

              // Check if backend returned config with missing fields
              if (data.config) {
                setShowManualInput(true);
                const config = data.config;
                setS3Credentials((prev) => ({
                  ...prev,
                  region: config.region || prev.region,
                  bucketName: config.bucket || prev.bucketName,
                  folderPath: config.folder_path || prev.folderPath,
                  accessKeyId: config.access_key_id || prev.accessKeyId,
                  secretAccessKey:
                    config.secret_access_key || prev.secretAccessKey,
                }));
                notifications.show({
                  title: "Additional Information Required",
                  message:
                    data.error ||
                    "Some fields are missing. Please complete the form manually.",
                  color: "orange",
                });
              } else {
                setShowManualInput(true);
                notifications.show({
                  title: "Validation Error",
                  message: data.error,
                  color: "red",
                });
              }

              setS3CredentialsErrors((prev) => ({
                ...prev,
                general: data.error,
              }));
              // Don't call onErrorMessage for S3 validation errors - error is shown in form
              // onErrorMessage(data.error);
              return;
            }

            // Also check if status is "failed" (even if no error field)
            if (data.status === "failed") {
              pollingActiveRef.current = false;
              shouldContinuePolling = false;
              if (pollTimeout) {
                clearTimeout(pollTimeout);
                pollTimeout = null;
              }

              setS3ProcessingStatus("failed");
              setS3ValidationLoading(false);
              setS3ProcessingActiveStep(0);
              setS3ValidationResult(null); // Clear any previous result
              const errorMessage = data.error || "Validation failed";

              // Check if backend returned config with missing fields
              if (data.config) {
                setShowManualInput(true);
                const config = data.config;
                setS3Credentials((prev) => ({
                  ...prev,
                  region: config.region || prev.region,
                  bucketName: config.bucket || prev.bucketName,
                  folderPath: config.folder_path || prev.folderPath,
                  accessKeyId: config.access_key_id || prev.accessKeyId,
                  secretAccessKey:
                    config.secret_access_key || prev.secretAccessKey,
                }));
                notifications.show({
                  title: "Additional Information Required",
                  message:
                    errorMessage ||
                    "Some fields are missing. Please complete the form manually.",
                  color: "orange",
                });
              } else {
                setShowManualInput(true);
                notifications.show({
                  title: "Validation Error",
                  message: errorMessage,
                  color: "red",
                });
              }

              setS3CredentialsErrors((prev) => ({
                ...prev,
                general: errorMessage,
              }));
              // Don't call onErrorMessage for S3 validation errors - error is shown in form
              // onErrorMessage(errorMessage);
              return;
            }

            // Success - job completed (status === "ready") with no error
            // Stop polling immediately
            pollingActiveRef.current = false;
            shouldContinuePolling = false;
            if (pollTimeout) {
              clearTimeout(pollTimeout);
              pollTimeout = null;
            }

            setS3ProcessingStatus("ready");
            setS3ProcessingActiveStep(4); // Step 4 shows Completed
            setS3ValidationLoading(false);

            // Store result only if no error
            if (!data.error) {
              setS3ValidationResult(data);
            } else {
              setS3ValidationResult(null);
            }

            // Show notification only once
            if (!notificationShownRef.current) {
              notificationShownRef.current = true;
              notifications.show({
                title: "Validation Complete",
                message: "S3 collection validation completed successfully",
                color: "green",
              });
            }
          },
          (error, errorData) => {
            // Error - stop polling
            pollingActiveRef.current = false;
            shouldContinuePolling = false;
            if (pollTimeout) {
              clearTimeout(pollTimeout);
              pollTimeout = null;
            }

            setS3ProcessingStatus("failed");
            setS3ValidationLoading(false);
            setS3ProcessingActiveStep(0);
            setS3ValidationResult(null); // Clear any previous result to prevent showing Collection Found

            // Check if backend returned config with missing fields
            if (errorData && errorData.config) {
              // Backend returned fields that need to be filled
              // Open manual form and populate fields from config
              setShowManualInput(true);

              const config = errorData.config;
              setS3Credentials((prev) => ({
                ...prev,
                region: config.region || prev.region,
                bucketName: config.bucket || prev.bucketName,
                folderPath: config.folder_path || prev.folderPath,
                accessKeyId: config.access_key_id || prev.accessKeyId,
                secretAccessKey:
                  config.secret_access_key || prev.secretAccessKey,
              }));

              setS3CredentialsErrors((prev) => ({
                ...prev,
                general: error,
              }));

              notifications.show({
                title: "Additional Information Required",
                message:
                  error ||
                  "Some fields are missing. Please complete the form manually.",
                color: "orange",
              });
            } else {
              setShowManualInput(true);
              // Regular error
              setS3CredentialsErrors((prev) => ({
                ...prev,
                general: error,
              }));
              notifications.show({
                title: "Validation Error",
                message: error,
                color: "red",
              });
            }
            // Don't call onErrorMessage for S3 validation errors - error is shown in form
            // onErrorMessage(error);
          },
          (progress) => {
            // Progress update from backend - use real status from backend
            // Check if we should stop polling
            if (!pollingActiveRef.current || !shouldContinuePolling) {
              return;
            }

            // Update progress and status from backend response
            setS3ValidationProgress(progress);
            if (progress.status) {
              setS3ProcessingStatus(progress.status);
              setS3ProcessingActiveStep(getStepFromStatus(progress.status));

              // Stop polling if status is ready or failed
              if (progress.status === "ready" || progress.status === "failed") {
                pollingActiveRef.current = false;
                shouldContinuePolling = false;
                if (pollTimeout) {
                  clearTimeout(pollTimeout);
                  pollTimeout = null;
                }
                return;
              }
            }
          },
        );

        // After request completes, check if we should continue polling
        // Only schedule next poll if still active; completion/failed status
        // is already handled via shouldContinuePolling flag in callbacks.
        if (pollingActiveRef.current && shouldContinuePolling) {
          // Wait 3 seconds before next poll
          pollTimeout = setTimeout(() => {
            if (pollingActiveRef.current && shouldContinuePolling) {
              poll();
            }
          }, 3000);
        }
      } catch (error) {
        console.error("Error polling job status:", error);
        // On error, wait before retrying (only if still active)
        if (pollingActiveRef.current && shouldContinuePolling) {
          pollTimeout = setTimeout(() => {
            if (pollingActiveRef.current && shouldContinuePolling) {
              poll();
            }
          }, 3000);
        }
      }
    };

    // Start polling immediately
    poll();

    return () => {
      pollingActiveRef.current = false;
      shouldContinuePolling = false;
      if (pollTimeout) {
        clearTimeout(pollTimeout);
      }
    };
  }, [s3JobId, s3ProcessingStatus]);

  // Handle S3 Credentials validation
  const handleS3CredentialsValidation = async () => {
    // Clear previous errors and reset state
    setS3CredentialsErrors({
      region: "",
      bucketName: "",
      accessKeyId: "",
      secretAccessKey: "",
      general: "",
    });
    setS3UriError("");
    setS3JobId(null);
    setS3ProcessingStatus(null);
    setS3ProcessingActiveStep(0);
    setS3ValidationProgress(null);
    setS3ValidationResult(null);
    notificationShownRef.current = false; // Reset notification flag
    pollingActiveRef.current = false; // Stop any active polling

    // Check if we're using URI or manual input
    if (!showManualInput) {
      // Validate URI field only
      if (!s3Uri || !s3Uri.trim()) {
        setS3UriError("S3 URI is required");
        notifications.show({
          title: "Error",
          message: "Please enter S3 URI",
          color: "red",
        });
        return;
      }

      // Basic validation for S3 URI format
      if (!s3Uri.trim().startsWith("s3://")) {
        setS3UriError("S3 URI must start with s3://");
        notifications.show({
          title: "Error",
          message: "S3 URI must start with s3://",
          color: "red",
        });
        return;
      }
    } else {
      // Validate manual input fields only
      const { region, bucketName, accessKeyId, secretAccessKey } =
        s3Credentials;

      const errors = {};
      if (!region || !region.trim()) {
        errors.region = "Region is required";
      }
      if (!bucketName || !bucketName.trim()) {
        errors.bucketName = "Bucket name is required";
      }
      if (!accessKeyId || !accessKeyId.trim()) {
        errors.accessKeyId = "Access Key ID is required";
      }
      if (!secretAccessKey || !secretAccessKey.trim()) {
        errors.secretAccessKey = "Secret Access Key is required";
      }

      if (Object.keys(errors).length > 0) {
        setS3CredentialsErrors(errors);
        notifications.show({
          title: "Error",
          message: "Please fill in all required fields",
          color: "red",
        });
        return;
      }
    }

    // Get collection timezone from user profile or use UTC
    let collectionTimezone = "UTC";
    try {
      const response = await fetch(URLs.USER_INFO_URL);
      if (response.ok) {
        const data = await response.json();
        collectionTimezone = data.display_timezone || "UTC";
      }
    } catch (error) {
      console.error("Error fetching timezone:", error);
    }

    setS3ValidationLoading(true);
    setS3ProcessingStatus("pending");
    setS3ProcessingActiveStep(0);

    // Prepare data for backend
    const requestData = !showManualInput
      ? {
          collection_uri: s3Uri.trim(),
          collection_timezone: collectionTimezone,
        }
      : {
          collection_timezone: collectionTimezone,
          access_key_id: s3Credentials.accessKeyId.trim(),
          secret_access_key: s3Credentials.secretAccessKey.trim(),
          region: s3Credentials.region.trim(),
          bucket_name: s3Credentials.bucketName.trim(),
          ...(s3Credentials.folderPath.trim() && {
            folder_path: s3Credentials.folderPath.trim(),
          }),
        };

    await validateS3Credentials(
      requestData,
      () => {}, // setLoading - handled separately
      (data) => {
        // Backend returns job_id and status
        if (data.job_id) {
          setS3JobId(data.job_id);
          // Status will be updated by polling
          if (data.status) {
            setS3ProcessingStatus(data.status);
            setS3ProcessingActiveStep(getStepFromStatus(data.status));
          }
        } else {
          // Should not happen with new backend
          setS3ValidationLoading(false);
          setS3ProcessingStatus("failed");
          setS3ProcessingActiveStep(0);
          const errorMessage = "Failed to get job_id from server";
          setS3CredentialsErrors((prev) => ({
            ...prev,
            general: errorMessage,
          }));
          notifications.show({
            title: "Validation Error",
            message: errorMessage,
            color: "red",
          });
          // Don't call onErrorMessage for S3 validation errors - error is shown in form
        }
      },
      (error, errorData) => {
        // Error from API call
        setS3ValidationLoading(false);
        setS3ProcessingStatus("failed");
        setS3ProcessingActiveStep(0);

        if (errorData && errorData.config) {
          // Open manual form and populate fields from config
          setShowManualInput(true);

          const config = errorData.config;
          setS3Credentials((prev) => ({
            ...prev,
            region: config.region || prev.region,
            bucketName: config.bucket || prev.bucketName,
            folderPath: config.folder_path || prev.folderPath,
            accessKeyId: config.access_key_id || prev.accessKeyId,
            secretAccessKey: config.secret_access_key || prev.secretAccessKey,
          }));

          // Set general error message
          setS3CredentialsErrors((prev) => ({
            ...prev,
            general: error,
          }));

          notifications.show({
            title: "Additional Information Required",
            message:
              error ||
              "Some fields are missing. Please complete the form manually.",
            color: "orange",
          });
        } else {
          setShowManualInput(true);
          // Regular error
          setS3CredentialsErrors((prev) => ({
            ...prev,
            general: error,
          }));
          notifications.show({
            title: "Validation Error",
            message: error,
            color: "red",
          });
        }
      },
    );
  };

  // Handle S3 credentials input changes
  const handleS3CredentialsChange = (field, value) => {
    setS3Credentials((prev) => {
      const nextCredentials = {
        ...prev,
        [field]: value,
      };

      if (showManualInput) {
        setS3Uri(buildS3UriFromCredentials(nextCredentials));
        if (s3UriError) {
          setS3UriError("");
        }
      }

      return nextCredentials;
    });

    // Clear field-specific error and general error when user starts typing
    if (s3CredentialsErrors[field] || s3CredentialsErrors.general) {
      setS3CredentialsErrors((prev) => ({
        ...prev,
        [field]: "",
        general: "",
      }));
    }

    // Reset validation result when any field changes
    if (s3ValidationResult) {
      setS3ValidationResult(null);
      setS3JobId(null);
      setS3ProcessingStatus(null);
      setS3ProcessingActiveStep(0);
      setS3ValidationProgress(null);
    }
  };

  // Keep S3 URI in sync when credentials change programmatically
  useEffect(() => {
    if (!showManualInput) {
      return;
    }
    const nextUri = buildS3UriFromCredentials(s3Credentials);
    if (nextUri !== s3Uri) {
      setS3Uri(nextUri);
    }
  }, [s3Credentials, showManualInput, s3Uri]);

  // Handle showing validation results
  const handleShowValidationResults = () => {
    // Only show results if we have valid result data and no errors
    if (
      s3ValidationResult &&
      !s3CredentialsErrors.general &&
      s3ProcessingStatus === "ready"
    ) {
      // Check if result has error field - if so, don't show results
      if (s3ValidationResult.error) {
        return; // Don't show results if there's an error
      }
      onShowCollectionFound(true);
      onContentCollectionFound(s3ValidationResult);
      // Reset validation result after showing results to allow new validation
      setS3ValidationResult(null);
      setS3JobId(null);
      setS3ProcessingStatus(null);
      setS3ProcessingActiveStep(0);
      setS3ValidationProgress(null);
    }
  };

  return (
    <Box id="verify-method-csv__screen" className={stampWrapClasses.wrap}>
      {/* Resource Type Selection Tabs - moved outside and above main content */}
      <Flex
        align={theme.flex.align.center}
        gap={theme.flex.gap.xs}
        mb={theme.margin.md}
        className="rb-main-content-verify"
        justify={theme.flex.justify.center}
        mt="2em"
      >
        <CustomButton
          id="resource-type-archive"
          text="ZIP or CSV Archive"
          onClick={() => handleResourceTypeChange("archive")}
          componentClasses={`${stampWrapClasses.resourceTabButton} ${
            resourceType === "archive" ? "active" : ""
          }`}
        />
        <CustomButton
          id="resource-type-s3-credentials"
          text="S3 Folder"
          onClick={() => handleResourceTypeChange("s3-credentials")}
          componentClasses={`${stampWrapClasses.resourceTabButton} ${
            resourceType === "s3-credentials" ? "active" : ""
          }`}
        />
      </Flex>

      <Box className={stampWrapClasses.wrapContent}>
        <Flex
          direction="column"
          gap={theme.flex.gap.large}
          justify={theme.flex.justify.start}
          align={theme.flex.align.center}
          classNames={{
            root: stampWrapClasses.wrapContentContainer,
          }}
        >
          {isAuthenticated ? (
            <>
              {/* Archive Upload Section */}
              {resourceType === "archive" && (
                <Dropzone
                  w="100%"
                  onDrop={handleDrop}
                  justify={theme.flex.justify.center}
                  classNames={{
                    root: stampWrapClasses.dropzone,
                  }}
                  onReject={(files) => console.log("rejected files", files)}
                  accept={[
                    "application/zip",
                    "application/x-zip-compressed",
                    "application/octet-stream",
                    "text/csv",
                  ]}
                  {...props}
                >
                  <Box className={stampWrapClasses.dropzoneContentBox}>
                    <Group
                      classNames={{
                        root: stampWrapClasses.dropzoneGroup,
                      }}
                      justify={theme.flex.justify.center}
                      gap={theme.flex.gap.xs}
                      mih={50}
                    >
                      <Box>
                        <Button
                          size="xl"
                          color={theme.customBackgrounds.button}
                          classNames={{
                            root: stampWrapClasses.dropzoneButton,
                          }}
                        >
                          <Flex
                            align={theme.flex.align.center}
                            gap={
                              tabletMediaQuery
                                ? theme.flex.gap.medium
                                : theme.flex.gap.large
                            }
                          >
                            <Dropzone.Accept>
                              <IconUpload
                                className={stampWrapClasses.dropzoneIcon}
                                stroke={1}
                              />
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                              <IconX
                                size={52}
                                color="var(--mantine-color-red-6)"
                                stroke={1.5}
                              />
                            </Dropzone.Reject>
                            <Dropzone.Idle>
                              <IconFolderOpen
                                className={stampWrapClasses.dropzoneIcon}
                                stroke={1}
                              />
                            </Dropzone.Idle>
                            <Text
                              classNames={{
                                root: stampWrapClasses.dropzoneText,
                              }}
                            >
                              Load ZIP or Long CSV archive
                            </Text>
                          </Flex>
                        </Button>
                        <Text
                          c={theme.customColors.blackLight}
                          inline
                          mt={theme.margin.smTight}
                          classNames={{
                            root: stampWrapClasses.dropzoneSubText,
                          }}
                        >
                          Or just drag and drop file here
                        </Text>

                        <Text
                          c={theme.customColors.blackLight}
                          inline
                          mt={theme.margin.smTight}
                          classNames={{
                            root: stampWrapClasses.dropzoneSubText2,
                          }}
                        >
                          (You can only load an archive with the{" "}
                          <Anchor
                            c="dimmed"
                            href="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-verify"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={stampWrapClasses.anchorPointerEvents}
                          >
                            {" "}
                            appropriate structure
                          </Anchor>
                          )
                        </Text>
                      </Box>
                    </Group>

                    {preview && (
                      <Box>
                        File Loaded: <b>{preview}</b>
                        <IconXboxX
                          onClick={(event) => {
                            event.stopPropagation();
                            handleClickRemoveFile();
                          }}
                          className={stampWrapClasses.dropzoneRemoveIcon}
                          stroke={1.5}
                        />
                      </Box>
                    )}

                    {newData && (
                      <Container
                        size="26rem"
                        classNames={{
                          root: "rb-text-left w-100",
                        }}
                      >
                        <form onSubmit={form.onSubmit(handleFormSubmit)}>
                          <TextInput
                            pb="md"
                            type="hidden"
                            key={form.key("new_csv")}
                            {...form.getInputProps("new_csv")}
                          />

                          <CustomButton
                            text="Verify"
                            type="submit"
                            loading={loading}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                            componentClasses={`${stampWrapClasses.resourceSubmitButton} collection-validate-submit`}
                          />
                        </form>
                      </Container>
                    )}
                  </Box>
                </Dropzone>
              )}

              {/* S3 Credentials Section */}
              {resourceType === "s3-credentials" && (
                <Box className={stampWrapClasses.s3Section}>
                  <Flex
                    direction="column"
                    align="center"
                    gap={theme.flex.gap.md}
                  >
                    <LogoAwsSvg className={stampWrapClasses.s3Icon} />
                    <Text className={stampWrapClasses.s3Title}>
                      S3 Configuration
                    </Text>

                    {/* S3 URI Input - always visible */}
                    <Flex className={stampWrapClasses.s3FormContainer}>
                      <TextInput
                        placeholder="s3://bucket-name/path?access=AKIAXX&secret=XXXXXX&region=us-east-1"
                        className={stampWrapClasses.s3Input}
                        name="s3-uri"
                        value={s3Uri}
                        disabled={showManualInput}
                        onChange={(event) => {
                          setS3Uri(event.currentTarget.value);
                          // Clear error when user starts typing
                          if (s3UriError) {
                            setS3UriError("");
                          }
                          // Clear general error
                          if (s3CredentialsErrors.general) {
                            setS3CredentialsErrors((prev) => ({
                              ...prev,
                              general: "",
                            }));
                          }
                          // Reset validation result when URI changes
                          if (s3ValidationResult) {
                            setS3ValidationResult(null);
                            setS3JobId(null);
                            setS3ProcessingStatus(null);
                            setS3ProcessingActiveStep(0);
                            setS3ValidationProgress(null);
                          }
                        }}
                        error={s3UriError}
                      />
                    </Flex>

                    {/* Manual Input Checkbox */}
                    <Checkbox
                      label="Enter Configuration Manually"
                      className={
                        collectionSelectClasses.collectionSelectCheckbox
                      }
                      checked={showManualInput}
                      onChange={(event) => {
                        const isManual = event.currentTarget.checked;
                        setShowManualInput(isManual);
                        if (isManual) {
                          const parsedCredentials =
                            s3Uri && s3Uri.trim()
                              ? parseS3UriToCredentials(s3Uri, s3Credentials)
                              : null;
                          if (parsedCredentials) {
                            setS3Credentials(parsedCredentials);
                          } else {
                            setS3Uri(
                              hasAnyS3FieldValue(s3Credentials)
                                ? buildS3UriFromCredentials(s3Credentials)
                                : "",
                            );
                          }
                        }
                        // Clear all errors when switching between URI and manual input
                        setS3UriError("");
                        setS3CredentialsErrors({
                          region: "",
                          bucketName: "",
                          folderPath: "",
                          accessKeyId: "",
                          secretAccessKey: "",
                          general: "",
                        });
                        // Reset validation result when switching input modes
                        if (s3ValidationResult) {
                          setS3ValidationResult(null);
                          setS3JobId(null);
                          setS3ProcessingStatus(null);
                          setS3ProcessingActiveStep(0);
                          setS3ValidationProgress(null);
                        }
                      }}
                    />

                    {/* Manual Input Form - shown when checkbox is checked */}
                    {showManualInput && (
                      <Flex className={stampWrapClasses.s3FormContainer}>
                        <TextInput
                          label="AWS Region (e.g., us-east-1)"
                          placeholder="Enter AWS Region (e.g., us-east-1)"
                          className={stampWrapClasses.s3Input}
                          name="s3-region"
                          value={s3Credentials.region}
                          onChange={(event) =>
                            handleS3CredentialsChange(
                              "region",
                              event.currentTarget.value,
                            )
                          }
                          error={s3CredentialsErrors.region}
                        />
                        <TextInput
                          label="Bucket Name"
                          placeholder="Enter Bucket Name"
                          className={stampWrapClasses.s3Input}
                          name="s3-bucket-name"
                          value={s3Credentials.bucketName}
                          onChange={(event) =>
                            handleS3CredentialsChange(
                              "bucketName",
                              event.currentTarget.value,
                            )
                          }
                          error={s3CredentialsErrors.bucketName}
                        />
                        <TextInput
                          label="Folder Path"
                          placeholder="Enter Folder Path (optional)"
                          className={stampWrapClasses.s3Input}
                          name="s3-folder-path"
                          value={s3Credentials.folderPath}
                          onChange={(event) =>
                            handleS3CredentialsChange(
                              "folderPath",
                              event.currentTarget.value,
                            )
                          }
                          error={s3CredentialsErrors.folderPath}
                        />
                        <TextInput
                          label="Access Key ID"
                          placeholder="Enter Access Key ID"
                          className={stampWrapClasses.s3Input}
                          name="s3-access-key-id"
                          value={s3Credentials.accessKeyId}
                          onChange={(event) =>
                            handleS3CredentialsChange(
                              "accessKeyId",
                              event.currentTarget.value,
                            )
                          }
                          error={s3CredentialsErrors.accessKeyId}
                        />
                        <TextInput
                          label="Secret Access Key"
                          placeholder="Enter Secret Access Key"
                          className={stampWrapClasses.s3Input}
                          name="s3-secret-access-key"
                          value={s3Credentials.secretAccessKey}
                          onChange={(event) =>
                            handleS3CredentialsChange(
                              "secretAccessKey",
                              event.currentTarget.value,
                            )
                          }
                          error={s3CredentialsErrors.secretAccessKey}
                        />
                        <Text>
                          Not sure about the bucket details? Contact{" "}
                          <CustomAnchor
                            text="support@vbase.com"
                            href="mailto:support@vbase.com"
                            componentClasses="link"
                          />{" "}
                          for assistance.
                        </Text>
                      </Flex>
                    )}
                    {(s3ValidationLoading || s3ProcessingStatus) && (
                      <>
                        <Box pt={theme.spacing?.md || "1em"}>
                          <Stepper
                            color={theme.customColors.black}
                            active={s3ProcessingActiveStep}
                            allowNextStepsSelect={false}
                          >
                            <Stepper.Step
                              label="Pending"
                              description="Initializing validation job"
                            />
                            <Stepper.Step
                              label="Loading"
                              description="Loading files from S3"
                            />
                            <Stepper.Step
                              label="Processing"
                              description="Processing files"
                            />
                            <Stepper.Step
                              label="Ready"
                              description="Verifying collection against blockchain"
                            />
                            <Stepper.Completed>
                              <Text>Validation completed successfully</Text>
                            </Stepper.Completed>
                          </Stepper>
                        </Box>
                        {s3ProcessingStatus === "processing" &&
                          (() => {
                            const progressValue =
                              s3ValidationProgress &&
                              s3ValidationProgress.total_files > 0
                                ? (s3ValidationProgress.processed_files /
                                    s3ValidationProgress.total_files) *
                                  100
                                : 0;
                            return (
                              <Box
                                className={
                                  stampWrapClasses.ringProgressContainer
                                }
                              >
                                <RingProgress
                                  sections={[
                                    {
                                      value: progressValue,
                                      color: theme.customColors.blackLight,
                                    },
                                  ]}
                                  transitionDuration={250}
                                  label={
                                    <Text
                                      className={
                                        stampWrapClasses.ringProgressLabel
                                      }
                                    >
                                      {Math.round(progressValue)}%
                                    </Text>
                                  }
                                />
                              </Box>
                            );
                          })()}
                      </>
                    )}
                    {s3CredentialsErrors.general && (
                      <Text className="error">
                        {s3CredentialsErrors.general}
                      </Text>
                    )}
                    <CustomButton
                      text={
                        s3ValidationResult
                          ? "View Verification Results"
                          : "Verify S3 Folder"
                      }
                      loading={s3ValidationLoading}
                      onClick={
                        s3ValidationResult
                          ? handleShowValidationResults
                          : handleS3CredentialsValidation
                      }
                      componentClasses={stampWrapClasses.resourceSubmitButton}
                      disabled={
                        (!!s3JobId &&
                          s3ProcessingStatus !== "ready" &&
                          s3ProcessingStatus !== "failed") ||
                        s3ValidationLoading
                      }
                    />
                  </Flex>
                </Box>
              )}
            </>
          ) : (
            <Flex
              classNames={{
                root: stampWrapClasses.nonAuthSection,
              }}
            >
              <Text>
                Please{" "}
                <CustomAnchor
                  text="register"
                  componentClasses="link"
                  href={URLs.ACCOUNTS_SIGNUP_URL}
                ></CustomAnchor>{" "}
                for a free account and/or{" "}
                <CustomAnchor
                  text="log in"
                  componentClasses="link"
                  href={URLs.ACCOUNTS_LOGIN_URL}
                ></CustomAnchor>{" "}
                to verify a collection.
              </Text>
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
};

VerifyCollection.propTypes = {
  onShowCollectionFound: PropTypes.func.isRequired,
  onFileName: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  props: PropTypes.object,
  onContentCollectionFound: PropTypes.func,
  onFileType: PropTypes.func,
  isAuthenticated: PropTypes.bool.isRequired,
  setIsAuthenticated: PropTypes.func,
};
export default VerifyCollection;
